<template>
	<Modal
		title="Mismatch List"
		:closeOnOverlayClick="false"
		:visible="modalVisible"
		@close="onClose"
	>
		<template v-slot:header>
			Op Codes and RO Ops with
			<span class="mismatch-modal__description-bold"
				>Labor Rate Code {{ laborRateCode }}</span
			>
			have pricing discrepancies; manual update may be needed
		</template>

		<template v-slot:body>
			<div
				class="mismatch-modal__body"
				data-testid="mismatch-modal"
				ref="modalContainer"
				:noMaxHeight="true"
			>
				<PillTabs
					:tabs="tabs"
					:selectedTabIndex="currentTab"
					@tabChange="onTabChange"
					variant="defaultBg1"
					class="mismatch-modal__tab"
					ref="tablesContainer"
				>
					<template v-slot:1>
						<NotificationBlock
							v-if="showMismatchError"
							state="negative"
							iconClassName="icon-warning"
							title="Some Mismatch Updates Failed"
							:closeBtn="true"
							@close="updateMismatchError = false"
							class="mismatch-modal__notification"
							ref="notificationContainer"
						>
							<template v-slot:default>
								<div>
									Please try again. If this error persists,
									please contact support at
									dms_ps@dominiondms.com for help.
								</div>
							</template>
						</NotificationBlock>
						<div class="mismatch-modal__text-container">
							<span class="mismatch-modal__tab-text"
								>Select Op Code(s) to fix the mismatch.
								Make/Models with overridden amounts and/or fixed
								prices need to be updated in the Op Code and
								will not be updated here.</span
							>

							<ButtonClassic
								:labelText="labelUpdateMismatch"
								@click="onUpdateMismatch"
								variant="primary"
								:stopPropagation="true"
								:disabled="selectedOpCodesCount == 0"
							/>
						</div>
						<RowCollapseTable
							:recordset="opCodeMismatchRecordset"
							:columns="opCodeMismatchColumns"
							idColumn="id"
							:hasCheckbox="true"
							:expandedAll="true"
							childRecordsetPropName="items"
							:noResultMessageEnabled="true"
							@sort="onOpCodesSort"
							@onSelectAll="onOpCodesSelectAll"
							@onSelect="onOpCodesSelect"
							@opCodeModelClick="onClickOpCode"
							@checkboxMouseEnter="onCheckboxMouseEnter"
							@checkboxMouseLeave="onCheckboxMouseLeave"
							ref="opCodesTableContainer"
							class="mismatch-modal__opcode-table"
							:class="{
								'mismatch-modal__opcode-table--error': updateMismatchError
							}"
							noResultsPrimaryText="No Mismatches Available."
							noResultsSecondaryText="All Op Codes mismatches have been resolved."
							:noMaxHeight="true"
						>
							<template v-slot:checkboxContent="slotProps">
								<Tooltip
									class="mismatch-modal__checkbox-tooltip"
									:ref="`tooltip${slotProps.id}`"
									:trackedRefName="
										checkboxTooltipTrackedRef(
											slotProps.record.id
										)
									"
									:visible="
										checkboxTooltipVisible(
											slotProps.record.id
										)
									"
									placement="top-start"
									positionStrategy="fixed"
									:offset="[0, 8]"
								>
									{{ tooltipMessage(slotProps.record.id) }}
								</Tooltip>
							</template>
							<template v-slot:nestedRow="slotProps">
								<Table
									:recordset="slotProps.records"
									:columns="opCodeModelColumns"
									variant="nested"
									:responsive="false"
									:noMaxHeight="true"
								></Table>
							</template>
						</RowCollapseTable>
					</template>
					<template v-slot:2>
						<InfiniteScroller
							@scrollBoundary="getROOperationsNextPage"
							:hasMoreResults="hasMoreResults"
							:loading="nextPageLoading"
							:wheelElement="tableElement"
							:scrollElement="tableContainer"
							loaderPortalTarget="tableLoader"
						>
							<template v-slot:scrollDiv>
								<Table
									class="mismatch-modal__operation-table"
									:isTotalRow="false"
									:columns="laborRateRoOperationColumns"
									:recordset="operationMismatchItems"
									:responsive="false"
									:noResultMessageEnabled="true"
									@sort="onOperationsSort"
									@onOperationClick="onOperationClick"
									noResultsPrimaryText="No Mismatches Available."
									noResultsSecondaryText="All Repair Order Ops mismatches have been resolved."
									:noMaxHeight="true"
									@mounted="tableMounted"
									ref="table"
								>
									<template v-slot:created="slotProps">
										{{
											formatShortDate(
												slotProps.record
													.repairOrderDateCreated
											)
										}}
									</template>
								</Table>
							</template>
						</InfiniteScroller>
					</template>
				</PillTabs>
			</div>
		</template>

		<template v-slot:footer>
			<div class="mismatch-modal__footer">
				<ButtonSmall
					variant="text"
					labelText="Refresh List"
					iconClassName="icon-update"
					:iconLeading="true"
					@click="onRefresh"
				/>
				<ButtonSet>
					<ButtonClassic
						variant="secondary"
						labelText="Done"
						@click="onDone"
					/>
				</ButtonSet>
			</div>
		</template>
	</Modal>
</template>

<script>
import Modal from '@/components/shells/modal/modal.vue';
import { mapGetters, mapActions } from 'vuex';
import pageLoaderMixin from '@/mixins/page-loader-mixin';
import viewMixin from '@/mixins/view-mixin';
import PillTabs from '@/components/shells/tabs/pill-tabs.vue';
import RowCollapseTable from '@/components/shells/table/row-collapse-table.vue';
import opCodeMismatchColumns from '../labor-rate/op-code-mismatch-columns';
import opCodeModelColumns from '../labor-rate/op-code-model-columns';
import ButtonClassic from '@/components/forms/button/button-classic.vue';
import ButtonSet from '@/components/forms/button/button-set.vue';
import ButtonSmall from '@/components/forms/button/button-small.vue';
import Table from '@/components/shells/table/table.vue';
import laborRateRoOperationColumns from '../labor-rate/labor-rate-ro-operation-columns';
import headerToastMixin from '@/mixins/header-toast-mixin';
import NotificationBlock from '@/components/notifications/notification-block/notification-block.vue';
import DateFunctionMixin from '@/mixins/date-function-mixin';
import InfiniteScroller from '@/components/shells/infinite-scroller/infinite-scroller.vue';
import { DESC } from '@/components/shells/table/sort-directions';
import Tooltip from '@/components/shells/tooltip/tooltip.vue';

export default {
	name: 'LaborRateMismatchModal',
	mixins: [pageLoaderMixin, viewMixin, headerToastMixin, DateFunctionMixin],
	components: {
		Modal,
		PillTabs,
		RowCollapseTable,
		ButtonClassic,
		ButtonSet,
		ButtonSmall,
		Table,
		NotificationBlock,
		InfiniteScroller,
		Tooltip
	},
	data() {
		return {
			modalVisible: false,
			currentTab: 1,
			tableElement: undefined,
			tableContainer: undefined,
			currentPage: 1,
			operationLoading: false,
			scrolling: false,
			nextPageLoading: false,
			sortBy: 'repairOrderDateCreated',
			sortDirection: DESC,
			opCodeMismatchColumns: opCodeMismatchColumns,
			opCodeModelColumns: opCodeModelColumns,
			showCheckboxTooltipOpCodeId: undefined,
			laborRateRoOperationColumns: laborRateRoOperationColumns,
			updateMismatchError: false
		};
	},
	props: {
		laborRateId: {
			type: Number,
			default: 1
		}
	},
	async created() {
		await this.loadOpCodes();
		await this.loadOperations();
	},
	computed: {
		...mapGetters('app', ['appPageLoading']),
		...mapGetters('laborRates', [
			'laborRateCode',
			'opCodeMismatchResults',
			'operationMismatchItems',
			'selectedOpCodesCount',
			'opCodeMismatchCount',
			'operationMismatchesPageSize',
			'operationMismatchesCount',
			'operationMismatchesPageCount'
		]),
		tabs() {
			return [
				{
					index: 1,
					title: this.opCodeMismatchCount
						? 'Op Codes (' + `${this.opCodeMismatchCount}` + ')'
						: 'Op Codes (0)'
				},
				{
					index: 2,
					title: this.operationMismatchesCount
						? 'RO Ops (' + `${this.operationMismatchesCount}` + ')'
						: 'RO Ops (0)'
				}
			];
		},
		labelUpdateMismatch() {
			let label = 'Update Mismatch';
			label =
				this.selectedOpCodesCount > 0
					? label + ' (' + this.selectedOpCodesCount.toString() + ')'
					: label;
			return label;
		},
		showMismatchError() {
			return this.updateMismatchError;
		},
		opCodeMismatchRecordset() {
			return this.opCodeMismatchResults.items;
		},
		hasMoreResults() {
			let result = false;
			result =
				this.operationMismatchItems?.length <
				this.operationMismatchesCount;
			return result;
		}
	},
	methods: {
		...mapActions('laborRates', [
			'getOpCodeMismatchesAction',
			'getOperationMismatchesAction',
			'resetOperationMismatchesAction',
			'orderOpCodeMismatchesAction',
			'selectOpCodeAction',
			'selectAllOpCodesAction',
			'updateOpCodeMismatchesAction'
		]),
		async loadOpCodes() {
			this.showPageLoader();

			await this.getOpCodeMismatchesAction({
				laborRateId: this.laborRateId,
				companyId: this.companyId
			});

			this.modalVisible = true;
			this.hidePageLoader();
		},
		async loadOperations() {
			if (!this.appPageLoading) {
				if (!this.scrolling) {
					//this.showPageLoader();
					this.currentPage = 1;
					if (
						this.operationMismatchItems.length >
						this.operationMismatchesPageSize
					) {
						this.resetOperationMismatchesAction();
						// Set the scroll position to the top
						if (this.tableContainer) {
							this.tableContainer.scrollTop = 0;
						}
					}
				}
				await this.getOperationMismatchesAction({
					laborRateId: this.laborRateId,
					companyId: this.companyId,
					page: this.currentPage,
					sortBy: this.sortBy
						? `${this.sortBy} ${this.sortDirection}`
						: undefined
				});

				this.modalVisible = true;
				this.hidePageLoader();
				this.scrolling = false;
				this.nextPageLoading = false;
			}
		},
		tableMounted(tableReferences) {
			if (tableReferences) {
				this.tableContainer = tableReferences.tableContainer;
				this.tableElement = tableReferences.table;
			}
		},
		async onTabChange(tabIndex) {
			this.currentTab = tabIndex;
			if (tabIndex === 1) {
				await this.loadOpCodes();
			} else {
				await this.loadOperations();
			}
		},
		async onOpCodesSort(sort) {
			this.showPageLoader();
			await this.orderOpCodeMismatchesAction({
				sortDirection: sort?.sortDirection
			});
			this.hidePageLoader();
		},
		onOpCodesSelectAll(selected) {
			this.selectAllOpCodesAction({ selected: selected.checked });
		},
		onOpCodesSelect(selected) {
			this.selectOpCodeAction({
				opCodeId: selected.id,
				selected: selected.checked
			});
		},
		onClickOpCode(opCode) {
			const data = {
				url: `/Service/OpcodesView?Id=${opCode.id}&cid=${this.companyId}`,
				target: `opCodeMismatch${opCode.id}`
			};

			this.$emit('openWindow', data);
		},
		async onUpdateMismatch() {
			this.onOpCodeUpdateStart();

			await this.updateOpCodeMismatchesAction({
				laborRateId: this.laborRateId,
				companyId: this.companyId,
				errorHandler: this.onUpdateMismatchError
			});

			this.onOpCodeUpdateDone();

			this.showHeaderToast({
				title: 'Mismatch Successfully Updated',
				subtitle: undefined,
				message:
					'Selected Op Code/s have been corrected by applying updated Labor Rate.',
				state: 'positive',
				iconClassName: 'icon-circle-check',
				dismissibleTime: 5,
				siteLevel: true
			});
		},
		onUpdateMismatchError() {
			this.updateMismatchError = true;
			this.onOpCodeUpdateDone();
		},
		tooltipMessage() {
			return 'Includes overridden amount and/or fixed price; update from the Op Code screen.';
		},
		checkboxTooltipVisible(opCodeId) {
			return opCodeId === (this.showCheckboxTooltipOpCodeId || 0);
		},
		checkboxTooltipTrackedRef(opCodeId) {
			const refName = `singleSelectCheckbox${opCodeId}`;
			const table = this.$refs.opCodesTableContainer;
			let checkbox;

			if (
				table &&
				Object.prototype.hasOwnProperty.call(table.$refs, refName)
			) {
				checkbox = table.$refs[refName];

				if (Array.isArray(checkbox)) {
					checkbox = checkbox[0];
				}
			}
			return checkbox;
		},
		onCheckboxMouseEnter(record) {
			if (record.disabled){
				this.showCheckboxTooltipOpCodeId = record.id;
			}
		},
		onCheckboxMouseLeave() {
			this.showCheckboxTooltipOpCodeId = undefined;
		},
		async onRefresh() {
			await this.onTabChange(this.currentTab);
		},
		onDone() {
			this.onClose();
		},
		async onOperationsSort(objSort) {
			if (objSort) {
				this.sortBy = objSort.columnName;
				this.sortDirection = objSort.sortDirection;
				this.$nextTick(() => {
					this.loadOperations();
				});
			}
		},
		onOperationClick(operation) {
			//TODO: send message to AccessWeb to open operation in new tab
			const data = {
				url: `/Service/RepairOrderView?Id=${operation.repairOrderId}&cid=${this.companyId}&opId=${operation.id}`,
				target: `roOperationMismatch${operation.repairOrderId}`
			};

			this.$emit('openWindow', data);
		},
		onOpCodeUpdateStart() {
			this.$emit('opCodeUpdateStart');
		},
		onOpCodeUpdateDone() {
			this.$emit('opCodeUpdateDone');
		},
		async getROOperationsNextPage() {
			this.nextPageLoading = true;
			this.scrolling = true;
			this.currentPage += 1;
			await this.loadOperations();
		},
		onClose() {
			this.modalVisible = false;
			this.$emit('close');
		}
	}
};
</script>

<style lang="scss" scoped>
.mismatch-modal {
	&__description-bold {
		font-weight: $bold-font-weight;
	}

	&__body {
		height: calc(80vh - 170px);
	}

	&__tab {
		width: 850px;
		@include mq($until: md-l) {
			width: 700px;
		}
	}

	&__notification {
		margin-bottom: $gutter-md;
	}

	&__text-container {
		align-items: center;
		display: grid;
		grid-template-columns: 1fr auto;
		gap: $gutter-xl;
	}

	&__tab-text {
		font-size: $txt-font-size;
		color: var(--bg-1-fg-3);
	}

	&__footer {
		display: flex;
		justify-content: space-between;
		align-items: center;
	}

	&__opcode-table {
		margin-top: $gutter-lg;
		display: block;
		max-height: calc(80vh - 220px);
		&:deep(.row-collapsable__body) {
			overflow-y: auto;
			max-height: calc(80vh - 285px);
			@include mq($until: md-l) {
				max-height: calc(80vh - 300px);
			}
		}
		//Main table
		& :deep(.row-collapsable__th--opcode),
		& :deep(.row-collapsable__td--opcode) {
			max-width: 152px;
		}

		& :deep(.row-collapsable__th--description) {
			flex: 1;
		}

		//Nested table
		& :deep(.row-collapsable__nested) {
			padding-left: 56px;
			padding-right: $padding-md;
		}

		& :deep(.table__td--make-model) {
			width: 150px;
		}

		& :deep(.table__td--labor-hrs) {
			width: 82px;
		}

		& :deep(.table__td--description) {
			min-width: 268px;
			max-width: 268px;
			overflow: hidden;
			white-space: nowrap;
			text-overflow: ellipsis;

			@include mq($until: md-l) {
				max-width: 120px;
				min-width: 120px;
			}
		}

		& :deep(.table__th--rate),
		& :deep(.table__th--amount),
		& :deep(.table__th--fixed-price),
		& :deep(.table__td--rate),
		& :deep(.table__td--amount),
		& :deep(.table__td--fixed-price) {
			width: 82px;
			text-align: end;
		}
	}

	&__opcode-table--error {
		max-height: calc(80vh - 300px);

		&:deep(.row-collapsable__body) {
			overflow-y: auto;
			max-height: calc(80vh - 370px);
			@include mq($until: md-l) {
				max-height: calc(80vh - 400px);
			}
		}
	}

	&__operation-table {
		margin-top: $gutter-lg;

		//Main table
		& :deep(.table-container__tables) {
			max-height: calc(80vh - 270px);
			overflow-y: auto;
		}

		& :deep(.table__td--ro-number) {
			width: 94px;
		}

		& :deep(.table__td--operation) {
			width: 64px;
		}

		& :deep(.table__th--date-created),
		& :deep(.table__td--date-created) {
			width: 100px;
		}

		& :deep(.table__td--account-code) {
			width: 174px;
		}
		& :deep(.table__td--op-code) {
			width: 110px;
		}

		& :deep(.table__td--concern) {
			max-width: 268px;
			overflow: hidden;
			white-space: nowrap;
			text-overflow: ellipsis;

			@include mq($until: md-l) {
				max-width: 120px;
			}
		}
	}

	&__operation-table__infinite {
		max-height: calc(80vh - 340px);
		overflow-y: auto;
	}
}
</style>
